import { Box, Button, Sheet, Stack } from "@mui/joy";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { ProgramContentType } from "data/BlockComponent";
import PlainTextarea from "components/task/content/cell/textarea/PlainTextarea";
import { ElementType } from "data/CellComponent";
import * as _ from "lodash";
import { debounce } from "lodash";
import { theme } from "styles/theme";
import { invalidTextareaIndexAtom } from "recoil/invalidTextareaIndexAtom";
import { userAtom } from "recoil/userAtom";

export default function FeedbackCellV2({
  savedFeedback,
  blockIndex,
  setData,
  lineIndex,
  cellIndex,
  editorKey,
}: {
  savedFeedback?: string;
  blockIndex: number;
  setData: React.Dispatch<
    React.SetStateAction<ProgramContentType[] | undefined>
  >;
  lineIndex: number;
  cellIndex: number;
  editorKey?: string;
}) {
  const user = useRecoilValue(userAtom);
  const isCoach = user?.role !== "patient";

  const [feedback, setFeedback] = useState<string>(savedFeedback || "");
  const [isFocused, setIsFocused] = useState<boolean>(false);

  const [invalidTextareaIndexList, setInvalidTextareaIndexList] =
    useRecoilState(invalidTextareaIndexAtom);
  const indexKey = `${blockIndex}-${lineIndex}-${cellIndex}`;
  const isInvalidTextarea = invalidTextareaIndexList.includes(indexKey);

  const hadInvalidBorder = useRef(isInvalidTextarea);

  useEffect(() => {
    if (isInvalidTextarea) {
      hadInvalidBorder.current = true;
    }
  }, [isInvalidTextarea]);

  const updateInvalidIndexList = useCallback(
    (val: string) => {
      setInvalidTextareaIndexList((prev) => {
        if (!hadInvalidBorder.current) return prev;
        const trimmed = (val || "").trim();
        const exists = prev.includes(indexKey);
        if (trimmed.length > 0) {
          return exists ? prev : [...prev, indexKey];
        }
        return exists ? prev.filter((idx) => idx !== indexKey) : prev;
      });
    },
    [indexKey, setInvalidTextareaIndexList]
  );

  const handleChange = (value: string) => {
    setData((data) => {
      if (!data || !data[blockIndex]?.lines?.[lineIndex]?.[cellIndex]) {
        return data;
      }

      const originalCurrentContent = (data[blockIndex].lines[lineIndex][
        cellIndex
      ].content || {}) as ElementType;
      const originalValue = originalCurrentContent.value || "";

      if (originalValue === value) {
        return data;
      }

      const nextData = _.cloneDeep(data);
      const currentContent =
        nextData[blockIndex].lines[lineIndex][cellIndex].content || {};

      nextData[blockIndex].lines[lineIndex][cellIndex].content = {
        ...currentContent,
        value,
        htmlString: `<p>${value}</p>`,
      };

      return nextData;
    });

    updateInvalidIndexList(value);
  };

  const debouncedSaveText = debounce(handleChange, 500);

  useEffect(() => {
    debouncedSaveText(feedback);

    return () => debouncedSaveText.cancel();
  }, [feedback, debouncedSaveText]);

  return (
    <Stack direction="row" spacing={1} data-feedback-cell>
      <Box sx={{ flex: 1, minWidth: "0px" }}>
        <Sheet
          id={`textarea-${indexKey}`}
          sx={{
            width: "100%",
            height: "100%",
            backgroundColor: "white",
            px: "10px",
            py: "12px",
            borderRadius: "6px",
            borderWidth: "1.4px",
            borderStyle: "solid",
            borderColor: isInvalidTextarea
              ? theme.vars.palette.Border.HighlightWithAlert
              : isFocused
                ? theme.vars.palette.Border.Highlight
                : theme.vars.palette.Border.Weak,
            position: "relative",
          }}
        >
          <PlainTextarea
            placeholder="코치 선생님에게 채팅으로 전달돼요"
            initialValue={savedFeedback}
            handleChange={(value) => {
              setFeedback(value);
              updateInvalidIndexList(value);
            }}
            isFocused={isFocused}
            setIsFocused={setIsFocused}
            editorKey={editorKey}
          />
        </Sheet>
      </Box>

      <Button
        disabled={isCoach || feedback.trim().length === 0}
        sx={{ px: "10px" }}
        onClick={() => {
          setFeedback("");
          handleChange("");
        }}
      >
        <Box
          sx={{
            width: "24px",
            height: "24px",
          }}
        >
          <span className="material-symbols-rounded">send</span>
        </Box>
      </Button>
    </Stack>
  );
}
